import { useRef } from 'react';

import { ApolloError } from '@apollo/client';
import { IDBSmartCode } from '@netfront/gelada-identity-library';
import { FormFieldProps, useToast } from '@netfront/ui-library';
import {  useCreateGroup, useUpdateGroup, useDeleteGroup, useAnswerGroupCustomFields, useUpdateSmartCodes } from 'hooks';
import { ICustomFieldResponses } from 'types';

const useUpsertGroup = ({ onUpdate }: { onUpdate: () => void }) => {
  const { handleToastError, handleToastSuccess } = useToast();
  const responsesRef = useRef<{value: ICustomFieldResponses[]}>({value: [] });
  const groupIdRef = useRef<{value: number| undefined }>({value: undefined });

  const handleGetError = (error: ApolloError) => {
    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const cleanUpAndReturn = (type: string): void => {
    handleToastSuccess({
      message: `Group successfully ${type}.`,
    });
    groupIdRef.current.value = undefined;
    responsesRef.current.value = [];
    onUpdate();
  };

  const { handleUpdateSmartCodes, isLoading: isUpdateSmartCodesLoading = false } = useUpdateSmartCodes({
    onError: handleGetError,
  });

  const { handleUpdateGroup, isLoading: isUpdateGroupLoading = false } = useUpdateGroup({
    onCompleted: () => {
      cleanUpAndReturn('updated');
    },
    onError: handleGetError,
  });

  const { handleCreateGroup, isLoading: isCreateGroupLoading = false } = useCreateGroup({
    onCompleted: ({ group }) => {
      if (responsesRef.current.value.length > 0) {
        void handleAnswerGroupCustomFields({
          responses: responsesRef.current.value,
          groupId: group.id,
        });
      } else {
        cleanUpAndReturn('created');
      }
    },
    onError: handleGetError,
  });


  const { handleDeleteGroup, isLoading: isDeleteGroupLoading = false } = useDeleteGroup({
    onCompleted: () => {
      cleanUpAndReturn('deleted');
    },
    onError: handleGetError,
  });

  const { handleAnswerGroupCustomFields, isLoading: isAnswerGroupCustomFieldsLoading = false } = useAnswerGroupCustomFields({
    onCompleted: () => {
      if (!groupIdRef.current.value)  {
        cleanUpAndReturn('created');
      }
    },
    onError: (error) => {
      handleToastError({
        error,
      });
    },
  });

  const handleUpsertGroup = ({ 
    groupId,
    item,
    projectId, 
    responses = [],
  }: {
    groupId?: number;
    item: FormFieldProps;
    projectId: string;
    responses?: ICustomFieldResponses[];
  }) => {

    const { 
      groupTypeId,
      name,
      phoneNumber,
      website,
      city,
      isActive = false,
      line1,
      line2,
      line3,
      postcode,
      state,
      smartCodes = [],
    } = item;

    responsesRef.current.value = responses;
    groupIdRef.current.value = groupId;

    if (!groupId) {
      void handleCreateGroup({
        group: {
          groupTypeId: groupTypeId ? Number(groupTypeId) : undefined,
          name,
          phoneNumber,
          projectId,
          website,
          address: {
            city,
            country: 'AU',
            line1,
            line2,
            line3,
            postcode,
            state,
          }
        }
      });
    } else {

      void handleUpdateGroup({
        group: {
          groupId,
          groupTypeId: groupTypeId ? Number(groupTypeId) : undefined,
          name,
          phoneNumber,
          website,
          status: isActive ? 'ACTIVE' : 'INACTIVE',
        },
        address: {
          city,
          country: 'AU',
          line1,
          line2,
          line3,
          postcode,
          state,
        },
      });

      void handleAnswerGroupCustomFields({
        responses,
        groupId: groupId,
      });

      void handleUpdateSmartCodes({
        smartCodes: (smartCodes as IDBSmartCode[]).map(({ code, smartCodeId }) => ({
          code,
          id: smartCodeId,
        })),
      })
    }
  };

  return {
    isLoading: 
      isCreateGroupLoading || 
      isDeleteGroupLoading ||
      isUpdateGroupLoading ||
      isAnswerGroupCustomFieldsLoading ||
      isUpdateSmartCodesLoading,
    handleUpsertGroup,
    handleDeleteGroup,
  };
};
export { useUpsertGroup };
